/*
  MEEM ARABIC — ENLARGED WORKSHEET CAROUSEL

  Features:
  - Previous/next arrows
  - Image counter
  - Mobile swipe navigation
  - Keyboard arrow navigation
  - Full worksheet preview without cropping
  - Works with existing homepage image carousels
*/

(function () {
  'use strict';

  const STYLE_ID = 'meem-enlarged-carousel-style';

  function addStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;

    style.textContent = `
      .mh-worksheet-dialog.mec-enabled[open] {
        position: fixed;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        overflow: hidden;
        box-sizing: border-box;
        max-height: 94dvh;
        max-width: 96vw;
      }

      .mh-worksheet-dialog.mec-enabled:not([open]) {
        display: none !important;
      }

      .mh-worksheet-dialog.mec-enabled .mh-worksheet-dialog-toolbar {
        width: 100%;
        flex: 0 0 auto;
        position: relative;
        top: auto;
      }

      .mh-worksheet-dialog.mec-enabled img[data-mh-preview-img] {
        display: block;
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: calc(94dvh - 112px);
        object-fit: contain;
        margin: auto;
        min-height: 0;
        user-select: none;
        -webkit-user-drag: none;
        touch-action: pan-y;
      }

      /* NAVIGATION ARROWS */

      .mh-worksheet-dialog.mec-enabled .mec-nav {
        position: absolute;
        z-index: 5;
        top: 52%;
        transform: translateY(-50%);
        width: 46px;
        height: 46px;
        padding: 0;
        display: grid;
        place-items: center;
        border: 1px solid #e5d9f1;
        border-radius: 50%;
        background: rgba(255, 253, 247, .94);
        color: #56358b;
        box-shadow: 0 3px 18px rgba(40, 33, 55, .15);
        font-size: 27px;
        line-height: 1;
        cursor: pointer;
      }

      .mh-worksheet-dialog.mec-enabled .mec-nav:hover {
        background: #f9dc78;
      }

      .mh-worksheet-dialog.mec-enabled .mec-nav:focus-visible {
        outline: 3px solid #56358b;
        outline-offset: 2px;
      }

      .mh-worksheet-dialog.mec-enabled .mec-prev {
        left: 10px;
      }

      .mh-worksheet-dialog.mec-enabled .mec-next {
        right: 10px;
      }

      /* IMAGE COUNTER */

      .mh-worksheet-dialog.mec-enabled .mec-counter {
        position: absolute;
        bottom: 10px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 5;
        padding: 7px 14px;
        border-radius: 20px;
        background: rgba(63, 36, 108, .91);
        color: white;
        font-size: 12px;
        font-weight: 700;
        white-space: nowrap;
        pointer-events: none;
      }

      .mh-worksheet-dialog.mec-enabled [hidden] {
        display: none !important;
      }

      /* MOBILE */

      @media (max-width: 749px) {

        .mh-worksheet-dialog.mec-enabled .mec-nav {
          width: 36px;
          height: 36px;
          font-size: 23px;
        }

        .mh-worksheet-dialog.mec-enabled .mec-prev {
          left: 4px;
        }

        .mh-worksheet-dialog.mec-enabled .mec-next {
          right: 4px;
        }

        .mh-worksheet-dialog.mec-enabled img[data-mh-preview-img] {
          max-height: calc(94dvh - 102px);
        }

      }
    `;

    document.head.appendChild(style);
  }

  function setup(root) {

    if (!root || root.dataset.mecReady === 'true') return;

    const dialog = root.querySelector('[data-mh-preview-dialog]');

    const image = dialog && dialog.querySelector('[data-mh-preview-img]');

    if (!dialog || !image) return;

    root.dataset.mecReady = 'true';

    dialog.classList.add('mec-enabled');

    /* PREVIOUS BUTTON */

    const previous = document.createElement('button');

    previous.type = 'button';
    previous.className = 'mec-nav mec-prev';

    previous.setAttribute(
      'aria-label',
      'Previous worksheet image'
    );

    previous.textContent = '‹';

    /* NEXT BUTTON */

    const next = document.createElement('button');

    next.type = 'button';
    next.className = 'mec-nav mec-next';

    next.setAttribute(
      'aria-label',
      'Next worksheet image'
    );

    next.textContent = '›';

    /* IMAGE COUNTER */

    const counter = document.createElement('span');

    counter.className = 'mec-counter';

    counter.setAttribute('aria-live', 'polite');

    dialog.append(previous, next, counter);

    let source = null;
    let photos = [];
    let index = 0;
    let swipeStart = null;

    /* IDENTIFY WHICH CAROUSEL WAS OPENED */

    function getOrigin(element) {

      if (!(element instanceof Element)) return null;

      const trigger = element.closest(
        '[data-mh-enlarge], .mh-wc-visual .mh-wc-image, .mh-proof-media[data-mh-proof-full]'
      );

      if (!trigger || !root.contains(trigger)) return null;

      return (
        trigger.closest('.mh-wc-visual') ||
        trigger.closest('[data-mh-proof-full]')
      );
    }

    /* CAPTURE THE ORIGINAL CAROUSEL */

    root.addEventListener('pointerdown', function (event) {

      const origin = getOrigin(event.target);

      if (origin) source = origin;

    }, true);

    root.addEventListener('click', function (event) {

      const origin = getOrigin(event.target);

      if (origin) source = origin;

    }, true);

    /* GET ALL IMAGES FROM THE SELECTED CAROUSEL */

    function allPhotos(origin) {

      if (!origin) return [];

      const slides = origin.querySelectorAll(
        '[data-mh-carousel-track] .mh-wc-slide[data-mh-large-url]'
      );

      if (slides.length) {

        return Array.from(slides, slide => ({
          src: slide.dataset.mhLargeUrl,
          alt: slide.querySelector('img')?.alt || 'Arabic worksheet preview'
        }));

      }

      const single = origin.querySelector('[data-mh-single-large]');

      if (single?.dataset.mhSingleLarge) {

        return [{
          src: single.dataset.mhSingleLarge,
          alt: single.querySelector('img')?.alt || 'Worksheet preview'
        }];

      }

      if (origin.dataset.mhProofFull) {

        return [{
          src: origin.dataset.mhProofFull,
          alt: origin.querySelector('img')?.alt || 'Worksheet preview'
        }];

      }

      return [];
    }

    /* COMPARE IMAGE URLS */

    function sameUrl(a, b) {

      try {

        return new URL(a, document.baseURI).href ===
               new URL(b, document.baseURI).href;

      } catch (_) {

        return a === b;

      }
    }

    /* UPDATE NAVIGATION AND COUNTER */

    function updateControls() {

      const multiple = photos.length > 1;

      previous.hidden = !multiple;
      next.hidden = !multiple;
      counter.hidden = !multiple;

      if (multiple) {

        counter.textContent =
          `${index + 1} / ${photos.length} · Swipe to browse`;

      }
    }

    /* SYNC WITH EXISTING HOMEPAGE PREVIEW */

    function sync() {

      if (!dialog.open) return;

      photos = allPhotos(source);

      if (photos.length) {

        const match = photos.findIndex(photo =>
          sameUrl(photo.src, image.getAttribute('src'))
        );

        if (match >= 0) {
          index = match;
        } else {
          index = 0;
        }

      }

      updateControls();
    }

    /* MOVE BETWEEN WORKSHEET IMAGES */

    function navigate(direction) {

      if (!dialog.open || photos.length < 2) return;

      index = (
        index + direction + photos.length
      ) % photos.length;

      const photo = photos[index];

      image.src = photo.src;
      image.alt = photo.alt;

      updateControls();
    }

    /* PREVIOUS ARROW */

    previous.addEventListener('click', event => {

      event.preventDefault();
      event.stopPropagation();

      navigate(-1);

    });

    /* NEXT ARROW */

    next.addEventListener('click', event => {

      event.preventDefault();
      event.stopPropagation();

      navigate(1);

    });

    /* KEYBOARD NAVIGATION */

    dialog.addEventListener('keydown', event => {

      if (
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight'
      ) {

        event.preventDefault();

        navigate(
          event.key === 'ArrowRight' ? 1 : -1
        );

      }

    });

    /* MOBILE SWIPE START */

    image.addEventListener('touchstart', event => {

      if (event.touches.length !== 1) {

        swipeStart = null;
        return;

      }

      swipeStart = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };

    }, { passive: true });

    /* MOBILE SWIPE END */

    image.addEventListener('touchend', event => {

      if (
        !swipeStart ||
        photos.length < 2 ||
        !event.changedTouches.length
      ) return;

      const dx =
        event.changedTouches[0].clientX - swipeStart.x;

      const dy =
        event.changedTouches[0].clientY - swipeStart.y;

      swipeStart = null;

      if (
        Math.abs(dx) > 45 &&
        Math.abs(dx) > Math.abs(dy) * 1.2
      ) {

        navigate(dx < 0 ? 1 : -1);

      }

    }, { passive: true });

    /* CANCEL SWIPE */

    image.addEventListener('touchcancel', () => {

      swipeStart = null;

    }, { passive: true });

    /* WATCH FOR THE PREVIEW OPENING OR CHANGING */

    const observer = new MutationObserver(sync);

    observer.observe(dialog, {
      attributes: true,
      attributeFilter: ['open']
    });

    observer.observe(image, {
      attributes: true,
      attributeFilter: ['src']
    });

    dialog.addEventListener('close', () => {

      swipeStart = null;

    });

    updateControls();
  }

  /* INITIALISE */

  function init() {

    addStyles();

    document.querySelectorAll('meem-homepage').forEach(setup);

  }

  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );

  } else {

    init();

  }

  document.addEventListener('shopify:section:load', init);

})();